<script setup lang="ts">
import { countries } from '~/assets/data/countries';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

const { getUser } = useAuthentication();
const { fromError } = useApiError();
const { submitClaimCertificateForm, startDownload } = useCertificate();
const { dispatchMessage } = useSystemMessages();
const { course } = useCourseState();

const processing = ref(false);
const errorMessage = ref('');
const user = ref<any | null>(null);

const emit = defineEmits<{
  (e: 'downloaded'): void;
}>();

const form = reactive({
  certificateType: 'cme_certificate',
  firstName: '',
  lastName: '',
  medicalDoctor: '',
  commerciallyBiased: '',
  accreditationBody: '',
  countryIsoCode: '',
  countryStateName: '',
  learningGoals: [] as { title: string; selected: boolean }[],
  practicalApplicationComment: '',
  implicitBiasComment: '',
  thirdPartyDisclaimer: false,
  rating: 0,
});

const fieldErrors = reactive({} as { [key: string]: string });
const FORM_FIELD_ID_PREFIX = 'course-claim-cme-credits-modal-field-';

const getFirstErrorKey = (errors: Object) => {
  const FORM_FIELDS_ORDER = [
    'certificateType',
    'firstName',
    'lastName',
    'typeOfDoctorOfMedicine',
    'isCommerciallyBiased',
    'accreditationBody',
    'countryIsoCode',
    'countryStateName',
    'achievedLearningGoals',
    'practicalApplicationComment',
    'implicitBiasComment',
    'userAgreedToDataSharing',
    'courseRating',
  ];

  const sortedKeys: Array<string> = Object.keys(errors).sort((a, b) => {
    return FORM_FIELDS_ORDER.indexOf(a) - FORM_FIELDS_ORDER.indexOf(b);
  });

  return sortedKeys.length > 0 ? sortedKeys[0] : null;
};

if (course.value?.learningGoals) {
  form.learningGoals = course.value?.learningGoals.map(g => {
    return { title: g, selected: false };
  });
}

const loadUser = async () => {
  processing.value = true;

  user.value = await getUser();
  form.firstName = user.value.firstName;
  form.lastName = user.value.lastName;

  processing.value = false;
};

const submit = async () => {
  for(let key in fieldErrors) {
    delete fieldErrors[key];
  }

  try {
    processing.value = true;
    errorMessage.value = '';

    const body = {
      certificateType: 'cme_certificate',
      firstName: form.firstName,
      lastName: form.lastName,
      countryIsoCode: form.countryIsoCode,
      countryStateName: form.countryStateName,
      typeOfDoctorOfMedicine: form.medicalDoctor,
      practicalApplicationComment: form.practicalApplicationComment ?? '',
      isCommerciallyBiased: form.commerciallyBiased && form.commerciallyBiased === '1',
      implicitBiasComment: form.implicitBiasComment ?? '',
      courseRating: form.rating,
      accreditationBody: form.accreditationBody,
      achievedLearningGoals: form.learningGoals.filter(g => g.selected).map(g => g.title),
      userAgreedToDataSharing: form.thirdPartyDisclaimer,
    } as {[key: string]: string | boolean | string[] | number };

    if (!course.value?.id) {
      return;
    }
    const response = await submitClaimCertificateForm(course.value.id, body);

    startDownload(response.data.value as BlobPart, `CME Certificate - ${course.value?.courseTitle}.pdf`);
    dispatchMessage('Success!', 'success', "You have claimed the certificate!");
    if (course.value){
      course.value.certificateStatus = 'pro-claimed';
    }
    emit('downloaded');

  } catch (error) {
    const apiError = fromError(error);

    if (apiError && apiError.statusCode === 422 && apiError.data) {
      const errorData = apiError.data.errors;

      for(let key in errorData) {
        fieldErrors[key] = errorData[key][0];
      }

      const errorFormFieldElement = document.getElementById(FORM_FIELD_ID_PREFIX + getFirstErrorKey(errorData));
      const modal = document.querySelector('.p-dialog-content');

      if (modal && errorFormFieldElement) {
        const headerOffset = 160;
        modal.scrollTo({
          top: errorFormFieldElement.offsetTop - headerOffset,
          behavior: 'smooth',
        });
      }

      errorMessage.value = 'Please check the form for errors.';
    } else {
      errorMessage.value = 'Something went wrong. Please try again later.';
    }
  } finally {
    processing.value = false;
  }
};

const op = ref();
const toggle = (event: Event) => {
  op.value.toggle(event);
};

onMounted(() => {
  loadUser();
});
</script>

<template>
  <div>
    <Card
      class="rounded-2xl p-0 text-neutral-90 shadow-medmastery mb-12"
      :pt="{
        body: {
          class: 'p-6'
        },
        content: {
          class: 'p-0'
        }
      }"
    >
      <template #content>
        <div class="flex items-center gap-4 mb-4">
          <!-- fixme: use just the svg for this icon instead of MIcon-->
          <MIcon
            icon="cme"
            class="!w-10 !h-10"
          />
          <div class="text-18 font-bold">
            This certificate comes with {{course?.cmeCredits}} CME credit{{course?.cmeCredits !== 1 ? 's' : ''}}.
          </div>
        </div>
        <div class="text-16 text-neutral-90 leading-150">
          By submitting this form, you will complete the <strong>{{course?.courseTitle}}</strong> and claim the associated CME credits. The date on the certificate will be the date of submission of this form.
        </div>
      </template>
    </Card>

    <Message
      v-if="errorMessage"
      class="mt-6"
      severity="error"
      :closable="false"
    >
      <strong>Claiming the certificate failed</strong>
      {{errorMessage}}
    </Message>

    <Divider class="my-6" />

    <div class="text-12 uppercase font-bold text-neutral-60 mb-6">
      Personal Information
    </div>

    <div class="grid grid-cols-2 gap-x-4 gap-y-6 mb-6">
      <div class="flex flex-col gap-1">
        <label
          :for="FORM_FIELD_ID_PREFIX + 'firstName'"
          class="text-14 text-neutral-70 font-bold pl-1"
        >
          First name *
        </label>
        <InputText
          :id="FORM_FIELD_ID_PREFIX + 'firstName'"
          v-model="form.firstName"
          :invalid="fieldErrors.firstName?.length > 0"
          :disabled="processing"
          aria-describedby="firstName-help"
        />
        <small
          v-if="fieldErrors.firstName?.length > 0"
          id="firstName-help"
          class="pl-2 text-12 text-red-50 mt-1"
        >
          {{fieldErrors.firstName}}
        </small>
        <small
          v-else
          id="firstName-help"
          class="pl-2 text-12 text-neutral-70 mt-1"
        >
          This is how your name will be displayed on the certificate.
        </small>
      </div>

      <div class="flex flex-col gap-1">
        <label
          :for="FORM_FIELD_ID_PREFIX + 'lastName'"
          class="text-14 text-neutral-70 font-bold pl-1"
        >
          Last name *
        </label>
        <InputText
          :id="FORM_FIELD_ID_PREFIX + 'lastName'"
          v-model="form.lastName"
          :invalid="fieldErrors.lastName?.length > 0"
          :disabled="processing"
          aria-describedby="lastName-help"
        />
        <small
          v-if="fieldErrors.lastName"
          id="lastName-help"
          class="pl-2 text-12 text-red-50 mt-1"
        >
          {{fieldErrors.lastName}}
        </small>
      </div>

      <div class="flex flex-col gap-1">
        <label
          :for="FORM_FIELD_ID_PREFIX + 'typeOfDoctorOfMedicine'"
          class="text-14 text-neutral-70 font-bold pl-1"
        >
          Are you a medical doctor (MD, DO, or equivalent) *
        </label>
        <Dropdown
          :id="FORM_FIELD_ID_PREFIX + 'typeOfDoctorOfMedicine'"
          v-model="form.medicalDoctor"
          placeholder="Please select"
          option-label="label"
          option-value="value"
          :invalid="fieldErrors.typeOfDoctorOfMedicine?.length > 0"
          :disabled="processing"
          :options="[
            {value: 'none', label: 'No, I am not'},
            {value: 'md', label: 'Yes, I am (MD)'},
            {value: 'do', label: 'Yes, I am (DO)'},
            {value: 'other', label: 'Yes, I am (other medical qualification)'}
          ]"
        />
        <small
          v-if="fieldErrors.typeOfDoctorOfMedicine"
          id="typeOfDoctorOfMedicine-help"
          class="pl-2 text-12 text-red-50 mt-1"
        >
          {{fieldErrors.typeOfDoctorOfMedicine}}
        </small>
      </div>

      <div class="flex flex-col gap-1">
        <label
          :for="FORM_FIELD_ID_PREFIX + 'isCommerciallyBiased'"
          class="text-14 text-neutral-70 font-bold pl-1"
        >
          Was this activity commercially biased? *
        </label>
        <Dropdown
          :id="FORM_FIELD_ID_PREFIX + 'isCommerciallyBiased'"
          v-model="form.commerciallyBiased"
          placeholder="Please select"
          option-label="label"
          option-value="value"
          :invalid="fieldErrors.isCommerciallyBiased?.length > 0"
          :disabled="processing"
          :options="[
            {value: '0', label: 'No, it was not biased'},
            {value: '1', label: 'Yes, it was biased'}
          ]"
        />
        <small
          v-if="fieldErrors.isCommerciallyBiased"
          id="isCommerciallyBiased-help"
          class="pl-2 text-12 text-red-50 mt-1"
        >
          {{fieldErrors.isCommerciallyBiased}}
        </small>
      </div>
    </div>

    <div class="flex flex-col gap-6">
      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between">
          <label
            :for="FORM_FIELD_ID_PREFIX + 'accreditationBody'"
            class="text-14 text-neutral-70 font-bold pl-1"
          >
            Which type of CME credits do you want to claim? *
          </label>

          <div
            class="flex items-center gap-1 text-blue-60 cursor-pointer"
            @click="toggle"
          >
            <span class="text-14 font-bold sm:block hidden">More Info</span>
            <FontAwesomeIcon
              icon="fa-circle-info"
              class="!w-4 !h-4"
            />
          </div>

          <OverlayPanel ref="op">
            <div class="max-w-[300px]">
              <div class="font-bold mb-2">
                European CME Credits
              </div>
              <div class=" text-14 text-neutral-60">
                Accredited by EACCME, these credits are recognized within Europe and support the continuing professional development of healthcare professionals in European countries.
              </div>

              <Divider />

              <div class="font-bold mb-2">
                AMA PRA Category 1 Credits
              </div>
              <div class="text-14 text-neutral-60">
                Accredited by ACCME, these credits are recognized in the United States and internationally, particularly for professionals working outside Europe.
              </div>
            </div>
          </OverlayPanel>
        </div>
        <Dropdown
          :id="FORM_FIELD_ID_PREFIX + 'accreditationBody'"
          v-model="form.accreditationBody"
          placeholder="Please select"
          option-label="label"
          option-value="value"
          :invalid="fieldErrors.accreditationBody?.length > 0"
          :disabled="processing"
          :options="[
            {value: 'eaccme', label: 'European CME credits'},
            {value: 'accme', label: 'AMA PRA Category 1 Credits™ (US or outside Europe)'}
          ]"
        />
        <small
          v-if="fieldErrors.accreditationBody?.length > 0"
          id="accreditationBody-help"
          class="pl-2 text-12 text-red-50 mt-1"
        >
          {{fieldErrors.accreditationBody}}
        </small>
        <small
          v-else
          id="accreditationBody-help"
          class="pl-2 text-12 text-neutral-70 mt-1"
        >
          European CME credits (ECMEC®s) are recognized in more than 20 European countries, and recommended for physicians based in Europe. <em>AMA PRA Category 1 Credits</em>™ are recommended for physicians based in the US or outside Europe.
        </small>
      </div>

      <div class="flex flex-col gap-1">
        <label
          :for="FORM_FIELD_ID_PREFIX + 'countryIsoCode'"
          class="text-14 text-neutral-70 font-bold pl-1"
        >
          In which country are you located? *
        </label>
        <Dropdown
          :id="FORM_FIELD_ID_PREFIX + 'countryIsoCode'"
          v-model="form.countryIsoCode"
          placeholder="Please select"
          option-label="name"
          option-value="code"
          editable
          :invalid="fieldErrors.countryIsoCode?.length > 0"
          :disabled="processing"
          :options="countries"
          :show-clear="!!form.countryIsoCode"
          append-to="self"
        >
          <template
            v-if="form.countryIsoCode"
            #value="{value}"
          >
            <div class="flex items-center">
              <MIcon
                sprite-location="/icons/country_flags.svg"
                :icon="value"
                class="rounded-sm mr-2 country-flag-icon--value"
              />

              <div>{{countries.find((item) => item.code === value)?.name}}</div>
            </div>
          </template>

          <template #option="{option}">
            <div class="flex items-center">
              <MIcon
                sprite-location="/icons/country_flags.svg"
                :icon="option.code"
                class="rounded-sm mr-2 country-flag-icon--option"
              />

              <div class="text-wrap">
                {{option.name}}
              </div>
            </div>
          </template>
        </Dropdown>
        <small
          v-if="fieldErrors.countryIsoCode?.length > 0"
          id="countryIsoCode-help"
          class="pl-2 text-12 text-red-50 mt-1"
        >
          {{fieldErrors.countryIsoCode}}
        </small>
      </div>

      <div class="flex flex-col gap-1">
        <label
          :for="FORM_FIELD_ID_PREFIX + 'countryStateName'"
          class="text-14 text-neutral-70 font-bold pl-1"
        >
          In which State do you live? (required for US citizens)
        </label>
        <InputText
          :id="FORM_FIELD_ID_PREFIX + 'countryStateName'"
          v-model="form.countryStateName"
          :invalid="fieldErrors.countryStateName?.length > 0"
          :disabled="processing"
          aria-describedby="countryStateName-help"
        />
        <small
          v-if="fieldErrors.countryStateName?.length > 0"
          id="countryStateName-help"
          class="pl-2 text-12 text-red-50 mt-1"
        >
          {{fieldErrors.countryStateName}}
        </small>
      </div>
    </div>

    <Divider class="my-6" />

    <div class="text-12 uppercase font-bold text-neutral-60 mb-6">
      Goals and Application
    </div>

    <div class="flex flex-col gap-4">
      <label class="text-14 text-neutral-70 font-bold ">
        Which of the learning goals did you achieve? Choose all that apply.
      </label>

      <div class="flex flex-col gap-2">
        <div
          v-for="(goal, index) of form.learningGoals"
          :key="index"
          class="flex items-center"
        >
          <Checkbox
            v-model="goal.selected"
            :binary="true"
            :input-id="`goal-` + index"
            :name="`goal-` + index"
          />
          <!-- eslint-disable vue/no-v-html -->
          <label
            :for="`goal-` + index"
            class="ml-2 f-14-regular"
            v-html="goal.title"
          />
          <!-- eslint-enable -->
        </div>
      </div>

      <Divider class="my-6" />

      <div class="flex flex-col gap-2">
        <label
          :for="FORM_FIELD_ID_PREFIX + 'practicalApplicationComment'"
          class="text-14 text-neutral-70 font-bold pl-1"
        >
          How will you apply what you learned in this course to your practice?
        </label>
        <Textarea
          :id="FORM_FIELD_ID_PREFIX + 'practicalApplicationComment'"
          v-model="form.practicalApplicationComment"
          :disabled="processing"
          aria-describedby="practicalApplicationComment-help"
          rows="5"
          class="resize-y"
        />
        <small
          v-if="fieldErrors.practicalApplicationComment?.length > 0"
          id="practicalApplicationComment-help"
          class="pl-2 text-12 text-red-50"
        >
          {{fieldErrors.practicalApplicationComment}}
        </small>
      </div>

      <Message
        :closable="false"
        severity="secondary"
        class="mb-6"
        :pt="{
          icon: {
            style: 'display: none'
          }
        }"
      >
        <template #messageicon />

        <div>
          <div class="text-18 font-bold mb-4">
            Implicit Bias
          </div>
          <div class="text-14">
            The attitudes, stereotypes, and feelings, either positive or negative, that affect our understanding, actions
            and decisions without conscious knowledge or control. Implicit bias is a universal phenomenon. When negative,
            implicit bias often contributes to unequal treatment and disparities in diagnosis, treatment decisions, levels
            of care and health care outcomes of patients based on race, ethnicity, gender identity, sexual orientation,
            age, disability, and other characteristics.
          </div>
        </div>
      </Message>

      <div class="flex flex-col gap-2">
        <label
          :for="FORM_FIELD_ID_PREFIX + 'implicitBiasComment'"
          class="text-14 text-neutral-70 font-bold pl-1"
        >
          Take a moment to consider how implicit bias may impact appropriate care of these patients. Please share your reflections.
        </label>
        <Textarea
          :id="FORM_FIELD_ID_PREFIX + 'implicitBiasComment'"
          v-model="form.implicitBiasComment"
          :disabled="processing"
          aria-describedby="implicitBiasComment-help"
          rows="5"
          class="resize-y"
        />
        <small
          v-if="fieldErrors.implicitBiasComment?.length > 0"
          id="implicitBiasComment-help"
          class="pl-2 text-12 text-red-50"
        >
          {{fieldErrors.implicitBiasComment}}
        </small>
      </div>

      <Divider class="my-6" />

      <div>
        <div class="flex gap-2 items-center">
          <MInputCheckbox
            :id="FORM_FIELD_ID_PREFIX + 'userAgreedToDataSharing'"
            v-model="form.thirdPartyDisclaimer"
            :disabled="processing"
          />
          <label :for="FORM_FIELD_ID_PREFIX + 'userAgreedToDataSharing'">
            I agree that the data submitted with this form, the date of submission, the
            name of the completed activity, and the quiz results are transmitted to our CME accrediting organization.
            *
          </label>
        </div>
        <small
          v-if="fieldErrors.userAgreedToDataSharing?.length > 0"
          id="implicitBiasComment-help"
          class="pl-2 text-12 text-red-50"
        >
          {{fieldErrors.userAgreedToDataSharing}}
        </small>
      </div>

      <div class="text-neutral-60 mt-4 text-14">
        European CME credits: European Union of Medical Specialists, 24 Rue de l'Industrie, 1040 Brussels, Belgium. <i>AMA
          PRA Category 1 Credits</i>™: CME Consultants, 8880 Benson Ave., Suite 122, Montclair, CA 91763-1661.
      </div>

      <Divider class="my-6" />

      <div
        :id="FORM_FIELD_ID_PREFIX + 'courseRating'"
        class="flex flex-col items-center gap-2"
      >
        <div>
          Please rate the learning experience of this course.
        </div>
        <StarRating
          v-model="form.rating"
          :disabled="processing"
        />
        <small
          v-if="fieldErrors.courseRating?.length > 0"
          id="implicitBiasComment-help"
          class="pl-2 text-12 text-red-50"
        >
          {{fieldErrors.courseRating}}
        </small>
      </div>

      <div class="flex justify-center mt-6 mb-4">
        <Button
          :loading="processing"
          class="font-bold !text-22 flex gap-2 px-12 bg-blue-30 text-neutral-90 border-none rounded-full"
          type="submit"
          @click="submit()"
        >
          <FontAwesomeIcon
            v-if="processing"
            icon="fa-circle-notch"
            class="animate-spin"
          />
          <FontAwesomeIcon
            v-else
            icon="fa-file-certificate"
          />
          <span>Claim certificate</span>
        </Button>
      </div>
    </div>
  </div>
</template>